import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Phone,
  CheckCircle2,
  Pause,
  Power,
  Inbox,
  UploadCloud,
  UsersRound,
  AlertTriangle,
  X,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT, useLang } from '@/i18n';
import { useLiveStatus } from '@/features/admin/useLiveStatus';
import { agentMetricsApi, type TeamDashboard } from '@/features/agent-metrics/agent-metrics.api';
import { useUploadersBreakdown, useHouseholdPromotionStats, useReportsByAgent } from '@/features/admin/reports.api';
import { householdApi } from '@/features/screening/household.api';
import { bookingsApi, type MissedBookingRow } from '@/features/bookings/bookings.api';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { AgentDetailModal } from './components/AgentDetailModal';
import type { LiveAgentRow } from '@/types/api';

/**
 * F5-T6a · Admin 02 — «Monitoreo en vivo» (contrato 2026-08-28).
 *
 * Fusión del tablero de entrada + live monitoring en UNA pantalla de tres
 * franjas: «Ahora mismo» (pulso) · «Piso de agentes» (detalle por persona,
 * el bloque más grande) · «Hogar · hoy» / «Exige acción hoy» (lo que se
 * decide hoy). Ningún dato queda en dos lugares.
 *
 * MUEREN de esta pantalla (contrato §dies): los anillos AdminMetricCard con
 * count-up · los KPIs de citas a 30 días (la serie mensual baja a Reportes,
 * ventana 7) · la distribución de niveles (vive en Progreso de agentes) ·
 * el snapshot del equipo (promedio redundante — propuesta default del doc) ·
 * la lista global de screenings (se muda a Reportes y al drill-down por
 * persona) · el rojo · el spinner centrado · el texto viejo de cron/servers.
 *
 * IMPORTANTE (LivePill del chrome): la píldora «en vivo» de la barra superior
 * (AdminChrome, intocable en T6a) se SUSCRIBE a ['admin','dashboard','team']
 * sin fetches propios; el dueño del poll era DailySnapshotPanel, que muere
 * acá. Esta página hereda la propiedad del poll (teamQ, cada 5 s) — sin eso
 * la píldora marcaría «detenido» con el sistema sano. El teamDashboard además
 * trae el target diario POR AGENTE (F-5 + overrides): el «/ {n} hoy» sale de
 * ahí, nunca hardcodeado (el 15 del dibujo es dato demo — decisión 4 del
 * contrato).
 */

type FloorState = 'onCall' | 'available' | 'paused' | 'off';

const STATE_ORDER: FloorState[] = ['onCall', 'available', 'paused', 'off'];

/** Colores de punto del catálogo cerrado (4). paused #9aa7b2 del dibujo se
 *  aproxima con nav-faint (#a9b6c1) — desviación declarada, sin hex nuevo. */
const DOT_CLASS: Record<FloorState, string> = {
  onCall: 'bg-nav-teal',
  available: 'bg-nav-sage',
  paused: 'bg-nav-faint',
  off: 'bg-nav-line',
};

const STATE_KEY: Record<FloorState, 'f5t6a.lm.state.onCall' | 'f5t6a.lm.state.available' | 'f5t6a.lm.state.paused' | 'f5t6a.lm.state.off'> = {
  onCall: 'f5t6a.lm.state.onCall',
  available: 'f5t6a.lm.state.available',
  paused: 'f5t6a.lm.state.paused',
  off: 'f5t6a.lm.state.off',
};

/**
 * Catálogo cerrado de 4 estados (contrato) sobre los 6 del backend:
 * on_call y documenting → «En llamada» (los dos son la llamada en curso; la
 * línea de contexto los distingue) · paused → «En pausa» · disconnected o
 * heartbeat viejo (>60 s) → «Desconectado» · active/idle → «Disponible».
 */
function floorState(a: LiveAgentRow, now: number): FloorState {
  const stale = !a.last_heartbeat_at || now - new Date(a.last_heartbeat_at).getTime() > 60_000;
  if (stale || a.current_state === 'disconnected' || a.current_state == null) return 'off';
  if (a.current_state === 'paused') return 'paused';
  if (a.current_state === 'on_call' || a.current_state === 'documenting') return 'onCall';
  return 'available';
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function hhmm(iso: string | null, lang: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(lang === 'es' ? 'es' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function mmss(totalSeconds: number | null): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—';
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function LiveMonitoringPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<FloorState | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const liveQ = useLiveStatus();

  // Dueño del poll de ['admin','dashboard','team'] (ver nota de cabecera):
  // alimenta la píldora del chrome Y trae el target diario por agente.
  const teamQ = useQuery<TeamDashboard>({
    queryKey: ['admin', 'dashboard', 'team'],
    queryFn: agentMetricsApi.teamDashboard,
    refetchInterval: 5000,
    placeholderData: keepPreviousData,
  });

  // «{n} intentos» por agente: /admin/reports/by-agent con la ventana de hoy.
  // No necesita el pulso de 5 s — 60 s alcanza para intentos.
  const today = localToday();
  const attemptsQ = useReportsByAgent(today, today);

  const uploadersQ = useUploadersBreakdown();

  const householdQ = useQuery({
    queryKey: ['admin', 'household-stats'],
    queryFn: () => householdApi.adminStats(),
    refetchInterval: 30_000,
  });

  // «Sub-screenings completados» hoy: promotion-stats con ventana de 1 día
  // (aprox. últimas 24 h — el endpoint no tiene corte por día NY exacto).
  // OJO PLATA: la respuesta trae estimatedRevenueUsd — NO se muestra nada de
  // dinero en esta pantalla (regla del contrato); solo se leen los conteos.
  const promoQ = useHouseholdPromotionStats(1);

  // «Citas perdidas sin atender»: la cola viva de /bookings/missed (lo
  // pendiente que exige acción hoy). Cap 100 del endpoint.
  const missedQ = useQuery({
    queryKey: ['bookings', 'missed', 'f5t6a-count'],
    queryFn: () => bookingsApi.missed(100, 0),
    refetchInterval: 60_000,
  });

  const now = Date.now();
  const agents = useMemo(() => liveQ.data?.agents ?? [], [liveQ.data]);

  const stateOf = useMemo(() => {
    const m = new Map<string, FloorState>();
    for (const a of agents) m.set(a.agent_id, floorState(a, now));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, liveQ.dataUpdatedAt]);

  const counts = useMemo(() => {
    const c: Record<FloorState, number> = { onCall: 0, available: 0, paused: 0, off: 0 };
    for (const a of agents) c[stateOf.get(a.agent_id) ?? 'off']++;
    return c;
  }, [agents, stateOf]);

  const subsInProgress = useMemo(
    () => agents.filter((a) => a.current_sub_screening_id != null).length,
    [agents],
  );

  const uploadersWorking = useMemo(() => {
    const per = uploadersQ.data?.perUploader ?? [];
    return per.filter((u) => (u.claimActive ?? 0) > 0 || (u.uploadedToday ?? 0) > 0).length;
  }, [uploadersQ.data]);

  // Target diario por agente (teamDashboard = F-5 + overrides individuales).
  const targetById = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of teamQ.data?.perAgent ?? []) m.set(String(p.agentId), p.todayTarget);
    return m;
  }, [teamQ.data]);

  const attemptsById = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of attemptsQ.data ?? []) m.set(String(r.agentId), r.callAttempts);
    return m;
  }, [attemptsQ.data]);

  const floor = useMemo(() => {
    const rows = filter ? agents.filter((a) => stateOf.get(a.agent_id) === filter) : agents;
    // En llamada primero, después disponibles, pausa y desconectados.
    return [...rows].sort(
      (a, b) =>
        STATE_ORDER.indexOf(stateOf.get(a.agent_id) ?? 'off') -
        STATE_ORDER.indexOf(stateOf.get(b.agent_id) ?? 'off'),
    );
  }, [agents, filter, stateOf]);

  const missedCount = useMemo(() => {
    const raw = missedQ.data;
    const list: MissedBookingRow[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    return list.length;
  }, [missedQ.data]);

  const membersToday = householdQ.data?.today.captured ?? 0;
  const subsDoneToday = promoQ.data?.subScreeningsSubmittedInWindow ?? 0;
  // «Sub-screenings esperando subida» del dibujo: no existe un conteo
  // NECESITA-SERVER: pendiente-de-subida SOLO de sub-screenings. Se usa el
  // pendiente global real (uploaders-breakdown.globalPending) con la
  // etiqueta ajustada a «Screenings esperando subida» — desviación declarada.
  const pendingUpload = uploadersQ.data?.globalPending ?? 0;

  const fichas: Array<{
    key: FloorState | 'subs' | 'uploaders';
    icon: LucideIcon;
    label: string;
    value: number;
    filter: FloorState | null;
  }> = [
    { key: 'onCall', icon: Phone, label: t('f5t6a.lm.card.onCall'), value: counts.onCall, filter: 'onCall' },
    { key: 'available', icon: CheckCircle2, label: t('f5t6a.lm.card.available'), value: counts.available, filter: 'available' },
    { key: 'paused', icon: Pause, label: t('f5t6a.lm.card.paused'), value: counts.paused, filter: 'paused' },
    { key: 'off', icon: Power, label: t('f5t6a.lm.card.off'), value: counts.off, filter: 'off' },
    { key: 'subs', icon: Inbox, label: t('f5t6a.lm.card.subs'), value: subsInProgress, filter: null },
    { key: 'uploaders', icon: UploadCloud, label: t('f5t6a.lm.card.uploaders'), value: uploadersWorking, filter: null },
  ];

  const floorCountLabel =
    floor.length === 1 ? t('f5t6a.lm.floorCountOne') : t('f5t6a.lm.floorCount', { n: floor.length });

  // ---- Estados de página (patrón compartido ventana 10 / chasis T2) ----

  if (liveQ.isLoading) {
    return (
      <div className="max-w-[1440px] mx-auto font-body text-nav-navy" aria-busy="true">
        {/* Esqueletos del tamaño de las fichas — NO spinner centrado. */}
        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(176px,1fr))]">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-[104px] rounded-xl" />
          ))}
        </div>
        <div className="mt-[34px] grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(286px,1fr))]">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-[150px] rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (liveQ.error && !liveQ.data) {
    return (
      <div className="max-w-[1440px] mx-auto">
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl">
          <ErrorState
            icon={<AlertTriangle className="w-6 h-6" aria-hidden />}
            title={t('f5t6a.lm.errTitle')}
            body={t('f5t6a.lm.errBody')}
            action={{ label: t('f5t6a.lm.retry'), onClick: () => void liveQ.refetch() }}
          />
        </div>
      </div>
    );
  }

  // El fallo del refresco de 5 s NO vacía la pantalla: keepPreviousData deja
  // los números quietos y la píldora del chrome (LivePill) dice la antigüedad
  // — «más seguro que borrarlos». Esta pantalla no muestra error propio.

  if (agents.length === 0) {
    // Estado vacío del contrato — reemplaza explícitamente el texto viejo de
    // «el cron del loader puede estar pausado». Salvia, un solo botón.
    return (
      <div className="max-w-[1440px] mx-auto">
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl max-w-[640px] mx-auto animate-fade-up-slow motion-reduce:animate-none">
          <EmptyState
            icon={<UsersRound className="w-6 h-6" aria-hidden />}
            title={t('f5t6a.lm.emptyTitle')}
            body={t('f5t6a.lm.emptyBody')}
            action={{ label: t('f5t6a.lm.emptyCta'), onClick: () => navigate('/admin/team') }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-[1440px] mx-auto font-body text-nav-navy animate-fade-up-slow motion-reduce:animate-none">
        {/* ============ FRANJA 1 · AHORA MISMO — el pulso, sin gráficos ============ */}
        <section aria-labelledby="lm-now">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3.5">
            <h2 id="lm-now" className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
              {t('f5t6a.lm.now')}
            </h2>
            <span className="flex items-center gap-1.5 text-[13px] text-nav-slate">
              {t('f5t6a.lm.shift')}
              {/* CONSERVA (precedente E-23): el refresh manual vivía en el
                  header viejo; el contrato ni lo dibuja ni lo mata. Queda
                  como icono discreto junto al turno. */}
              <button
                type="button"
                onClick={() => void liveQ.refetch()}
                disabled={liveQ.isFetching}
                aria-label={t('common.refresh')}
                title={t('common.refresh')}
                className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-40"
              >
                <RefreshCw className="w-4 h-4" aria-hidden />
              </button>
            </span>
          </div>

          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(176px,1fr))]">
            {fichas.map((f) => {
              const isFilter = f.filter != null;
              const active = isFilter && filter === f.filter;
              const Icon = f.icon;
              const chip = (
                <span
                  aria-hidden
                  className={cn(
                    'w-[30px] h-[30px] rounded-full flex items-center justify-center flex-none transition-colors duration-150 motion-reduce:transition-none',
                    isFilter
                      ? active
                        ? 'bg-white text-nav-teal'
                        : 'bg-nav-tealTint text-nav-teal'
                      : 'bg-nav-hair text-nav-slate',
                  )}
                >
                  <Icon className="w-4 h-4" strokeWidth={2} aria-hidden />
                </span>
              );
              const inner = (
                <>
                  <span className="flex items-start justify-between gap-2">
                    <span className="font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">
                      {f.value}
                    </span>
                    {chip}
                  </span>
                  <span className="block mt-3 text-[13px] leading-snug text-nav-slate">{f.label}</span>
                </>
              );
              const base =
                'text-left rounded-xl border-[1.5px] pt-[18px] px-5 pb-4 bg-white border-nav-card';
              if (!isFilter) {
                // Las 2 fichas de trabajo NO son botones: no hay nada que
                // filtrar con ellas (contrato).
                return (
                  <div key={f.key} className={base}>
                    {inner}
                  </div>
                );
              }
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter((cur) => (cur === f.filter ? null : f.filter))}
                  className={cn(
                    base,
                    'transition-all duration-150 motion-reduce:transition-none motion-reduce:transform-none',
                    'hover:-translate-y-[3px] hover:border-nav-teal hover:shadow-[0_10px_26px_rgba(18,43,68,.11)]',
                    'active:-translate-y-px active:scale-[0.99]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                    active && 'bg-nav-tealTint border-nav-teal',
                  )}
                >
                  {inner}
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ FRANJA 2 · PISO DE AGENTES — el bloque más grande ============ */}
        <section aria-labelledby="lm-floor" className="mt-[34px]">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3.5">
            <h2 id="lm-floor" className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
              {t('f5t6a.lm.floor')} · <span className="tabular-nums">{floorCountLabel}</span>
            </h2>
            {/* El conteo del piso se anuncia al filtrar; el refresco de 5 s NO
                se anuncia (sería un lector hablando sin parar). */}
            <span aria-live="polite" className="sr-only">
              {filter ? floorCountLabel : ''}
            </span>
            {filter && (
              <button
                type="button"
                onClick={() => setFilter(null)}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg font-head text-[13px] font-semibold text-nav-teal hover:bg-nav-tealTint transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
              >
                <X className="w-3.5 h-3.5" aria-hidden />
                {t('f5t6a.lm.clearFilter')}
              </button>
            )}
          </div>

          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(286px,1fr))]">
            {floor.map((a, i) => (
              <AgentFloorCard
                key={a.agent_id}
                agent={a}
                state={stateOf.get(a.agent_id) ?? 'off'}
                target={targetById.get(String(a.agent_id)) ?? null}
                attempts={attemptsById.get(String(a.agent_id)) ?? null}
                selected={selectedAgentId === a.agent_id}
                delaySec={i * 0.06}
                lang={lang}
                onOpen={() => setSelectedAgentId(a.agent_id)}
              />
            ))}
          </div>
        </section>

        {/* ============ FRANJA 3 · LO QUE SE DECIDE HOY ============ */}
        {(membersToday > 0 || subsDoneToday > 0 || missedCount > 0 || pendingUpload > 0) && (
          <section className="mt-[34px] grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
            {/* «Hogar · hoy» — se auto-oculta en cero (contrato). */}
            {(membersToday > 0 || subsDoneToday > 0) && (
              <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5">
                <h3 className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
                  {t('f5t6a.lm.household')}
                </h3>
                <div className="flex gap-8 mt-3.5">
                  <FootMetric value={membersToday} label={t('f5t6a.lm.members')} />
                  <FootMetric value={subsDoneToday} label={t('f5t6a.lm.subsDone')} />
                </div>
                <Link
                  to="/admin/reports"
                  className="inline-flex items-center gap-1 mt-[18px] font-body text-[13px] font-semibold text-nav-teal hover:text-nav-tealDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 rounded"
                >
                  {t('f5t6a.lm.breakdown')} <span aria-hidden className="text-[14px]">→</span>
                </Link>
              </div>
            )}

            {/* «Exige acción hoy» — LA ÚNICA tarjeta ámbar de la pantalla.
                «Esperando subida» vive acá por regla de plata: el atraso es
                trabajo del admin, no del agente. Tinta del dibujo #7a5410 →
                token nav-amberText (#8a6415), desviación declarada. */}
            {(missedCount > 0 || pendingUpload > 0) && (
              <div className="bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[22px] py-5">
                <h3 className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-amberText">
                  {t('f5t6a.lm.action')}
                </h3>
                <div className="flex gap-8 mt-3.5">
                  <FootMetric value={missedCount} label={t('f5t6a.lm.missed')} amber />
                  <FootMetric value={pendingUpload} label={t('f5t6a.lm.pendingUpload')} amber />
                </div>
                <Link
                  to="/admin/missed-bookings"
                  className="inline-flex items-center gap-1 mt-[18px] font-body text-[13px] font-semibold text-nav-amberText hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 rounded"
                >
                  {t('f5t6a.lm.openMissed')} <span aria-hidden className="text-[14px]">→</span>
                </Link>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Drill-down por persona (SE CONSERVA, contrato §dies): sus screenings
          de hoy — la lista filtrada que salió de esta pantalla. Si hiciera
          falta nombre de miembro, entra enmascarado y auditado ALLÍ, nunca en
          el piso (pantalla proyectable en pared). */}
      <AgentDetailModal agentId={selectedAgentId} onClose={() => setSelectedAgentId(null)} />
    </>
  );
}

/** Métrica del pie: número 28px/800 + etiqueta 12.5px. */
function FootMetric({ value, label, amber }: { value: number; label: string; amber?: boolean }): JSX.Element {
  return (
    <div className="min-w-0">
      <p className={cn('m-0 font-head text-[28px] font-extrabold leading-none tracking-[-0.02em] tabular-nums', amber ? 'text-nav-amberText' : 'text-nav-navy')}>
        {value}
      </p>
      <p className={cn('m-0 mt-1.5 text-[12.5px] leading-snug', amber ? 'text-nav-amberText' : 'text-nav-slate')}>{label}</p>
    </div>
  );
}

function AgentFloorCard({
  agent,
  state,
  target,
  attempts,
  selected,
  delaySec,
  lang,
  onOpen,
}: {
  agent: LiveAgentRow;
  state: FloorState;
  /** Target diario real (F-5 + override individual) del teamDashboard; null
   *  mientras carga — sin target no se dibuja ni denominador ni barra
   *  (nunca un 15 hardcodeado). */
  target: number | null;
  /** NECESITA-SERVER solo en granularidad: sale de reports/by-agent (hoy),
   *  refresco 60 s — no viaja en el snapshot de 5 s. null mientras carga. */
  attempts: number | null;
  selected: boolean;
  delaySec: number;
  lang: string;
  onOpen: () => void;
}): JSX.Element {
  const t = useT();
  const done = agent.submitted_today;
  const pct = target && target > 0 ? Math.min(100, (done / target) * 100) : 0;

  // Línea de contexto: dominio y paso, NUNCA la persona llamada (privacidad,
  // contrato). NECESITA-SERVER: el «dominio 4 de 9» del dibujo — el snapshot
  // live no trae el paso del wizard; se muestra el tipo de trabajo en curso.
  let ctx: string;
  if (state === 'onCall') {
    if (agent.current_sub_screening_id != null) ctx = t('f5t6a.lm.ctx.sub');
    else if (agent.current_state === 'documenting') ctx = t('f5t6a.lm.ctx.documenting');
    else ctx = t('f5t6a.lm.ctx.screening');
  } else if (state === 'paused') ctx = t('f5t6a.lm.ctx.paused');
  else if (state === 'off') ctx = t('f5t6a.lm.ctx.off');
  else ctx = t('f5t6a.lm.ctx.none');

  let since: string;
  if (state === 'onCall') since = mmss(agent.current_screening_seconds);
  else if (state === 'available') since = t('f5t6a.lm.since', { t: hhmm(agent.login_at, lang) });
  else if (state === 'paused') since = hhmm(agent.last_heartbeat_at, lang);
  else since = t('f5t6a.lm.left', { t: hhmm(agent.last_heartbeat_at, lang) });

  const attemptsLabel =
    attempts == null ? null : attempts === 1 ? t('f5t6a.lm.attemptsOne') : t('f5t6a.lm.attempts', { n: attempts });

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{ animationDelay: `${delaySec}s` }}
      className={cn(
        'text-left rounded-xl border-[1.5px] pt-[18px] px-5 pb-4 w-full',
        'animate-fade-up motion-reduce:animate-none',
        'transition-all duration-150 motion-reduce:transition-none motion-reduce:transform-none',
        'hover:-translate-y-[3px] hover:border-nav-teal hover:shadow-[0_10px_26px_rgba(18,43,68,.11)]',
        'active:-translate-y-px active:scale-[0.99]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
        selected ? 'bg-nav-tealTint border-nav-teal' : 'bg-white border-nav-card',
      )}
    >
      {/* Cabecera: avatar + nombre + estado (etiqueta de texto, no solo color) */}
      <span className="flex items-center gap-2.5 min-w-0">
        <span
          aria-hidden
          className="w-[34px] h-[34px] rounded-full bg-nav-navy text-white font-head text-[12px] font-bold flex items-center justify-center flex-none"
        >
          {initials(agent.full_name || agent.email)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-head text-[14px] font-bold truncate">{agent.full_name || agent.email}</span>
          <span className="flex items-center gap-1.5 text-[12px] font-semibold text-nav-slate tabular-nums">
            <span
              aria-hidden
              className={cn(
                'w-[7px] h-[7px] rounded-full flex-none',
                DOT_CLASS[state],
                // pulseDot SOLO en «en llamada»; prefers-reduced-motion lo apaga.
                state === 'onCall' && 'animate-pulse motion-reduce:animate-none',
              )}
            />
            <span className="truncate">
              {t(STATE_KEY[state])} · {since}
            </span>
          </span>
        </span>
      </span>

      {/* Separador + contexto («ahora») */}
      <span className={cn('block border-t mt-3.5 pt-[13px]', selected ? 'border-[#c9e2e2]' : 'border-nav-hair')}>
        <span className="block text-[12.5px] text-nav-slate truncate">{ctx}</span>

        {/* Fila inferior: conteo del día + intentos. Sin niveles y sin plata. */}
        <span className="flex items-baseline justify-between gap-2 mt-2">
          <span className="min-w-0">
            <span className="font-head text-[24px] font-extrabold leading-none tracking-[-0.02em] tabular-nums">{done}</span>
            {target != null && (
              <span className="ml-1 text-[12.5px] text-nav-slate tabular-nums">{t('f5t6a.lm.ofToday', { n: target })}</span>
            )}
          </span>
          {attemptsLabel && <span className="text-[12.5px] text-nav-slate tabular-nums flex-none">{attemptsLabel}</span>}
        </span>

        {/* Barra de progreso done/target — el refresco solo mueve el ancho. */}
        {target != null && (
          <span className="block h-[3px] rounded-sm bg-nav-hair mt-2.5 overflow-hidden" aria-hidden>
            <span
              className="block h-full bg-nav-teal rounded-sm transition-[width] duration-300 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none"
              style={{ width: `${pct}%` }}
            />
          </span>
        )}
      </span>
    </button>
  );
}
